import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  PageHeader, Table, Th, Td, TrEmpty, Badge, Btn, LoadingState, ErrorState,
} from '../components/ui';

interface Flag {
  id: string;
  flagId: string;
  enabled: boolean;
  rolloutPercent: number;
}

export function FeatureFlags() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<{ flags: Flag[] }>({
    queryKey: ['admin', 'feature-flags'],
    queryFn: () => api.get('/feature-flags'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.put(`/feature-flags/${id}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] }),
  });

  const flags = data?.flags ?? [];

  return (
    <div>
      <PageHeader
        title="Feature Flags"
        subtitle={data ? `${flags.length} defined` : undefined}
      />

      {isLoading && <LoadingState />}
      {error && <ErrorState message="Error loading flags" />}

      {!isLoading && !error && (
        <Table>
          <thead>
            <tr>
              <Th>Flag ID</Th>
              <Th>Status</Th>
              <Th>Rollout</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {flags.length === 0 ? (
              <TrEmpty cols={4} message="No flags defined" />
            ) : (
              flags.map(f => (
                <tr key={f.id} className="hover:bg-white/[0.02] transition-colors">
                  <Td className="font-mono text-xs">{f.flagId}</Td>
                  <Td>
                    {f.enabled ? (
                      <Badge variant="success">Enabled</Badge>
                    ) : (
                      <Badge variant="default">Disabled</Badge>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 max-w-[80px] h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${f.rolloutPercent}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 tabular-nums">{f.rolloutPercent}%</span>
                    </div>
                  </Td>
                  <Td>
                    <Btn
                      variant={f.enabled ? 'danger' : 'ghost'}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ id: f.flagId, enabled: !f.enabled })}
                    >
                      {f.enabled ? 'Disable' : 'Enable'}
                    </Btn>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
}
